// Port of ios/Sources/ViewModels/Permalinks.swift.
//
// Message permalinks (PARITY: "Copy link to a message" and "Open a permalink into the
// message"): the desktop shape, `https://server/{team-slug}/pl/{post-id}`.
//
// Reading one is two separate questions, and keeping them separate is the whole design. "Is
// this a message permalink at all?" is answered from the PATH alone. "Is it OUR server's?" is
// answered from the origin, and it is the boundary that keeps another deployment's link from
// being resolved against the signed-in account.
//
// The Swift file parses with `URLComponents`; this port uses the WHATWG `URL`, which lowercases
// the host and reports a default port as "" — `effectivePort` absorbs both. Path segments are
// percent-DECODED, as `URLComponents.path` hands them back.

/**
 * What an incoming `…/pl/…` URL turned out to be.
 * - `target`: a message on the configured server: resolve it and jump.
 * - `foreignServer`: permalink-shaped, but another deployment's — the reader is told, nothing is
 *   fetched.
 */
export type PermalinkIntake = { kind: 'target'; teamSlug: string; postId: string } | { kind: 'foreignServer' };

export function build(serverBase: string, teamSlug: string, postId: string): string {
  let base = serverBase;
  while (base.endsWith('/')) base = base.slice(0, -1);
  return base + '/' + teamSlug + '/pl/' + postId;
}

/** null = not a message permalink; nothing to route and nothing to say about it. */
export function intake(url: string, serverBase: string): PermalinkIntake | null {
  const link = components(url);
  if (!link) return null;
  const linkPath = pathSegments(link);
  if (!isPermalinkShaped(link, linkPath)) return null;

  const server = components(serverBase);
  if (!server || server.hostname === '') return { kind: 'foreignServer' };
  if (!sameOrigin(link, server)) return { kind: 'foreignServer' };

  // A configured base URL may carry a sub-path (`https://host/mattermost`), which every
  // permalink for that deployment repeats. A same-host link that does NOT repeat it
  // belongs to something else living on the same host, not to us.
  const serverPath = pathSegments(server);
  if (linkPath.length !== serverPath.length + 3) return { kind: 'foreignServer' };
  if (serverPath.some((segment, i) => linkPath[i] !== segment)) return { kind: 'foreignServer' };

  return { kind: 'target', teamSlug: linkPath[serverPath.length], postId: linkPath[serverPath.length + 2] };
}

/**
 * The path shape alone: at least one segment (the team slug), then `pl`, then exactly one
 * more (the post id). Anchored at the END rather than searched for, so a deployment
 * sub-path or a team whose slug happens to be "pl" cannot move the marker.
 */
function isPermalinkShaped(url: URL, segments: string[]): boolean {
  const scheme = url.protocol.toLowerCase();
  if (scheme !== 'http:' && scheme !== 'https:') return false;
  if (segments.length < 3 || segments[segments.length - 2] !== 'pl') return false;
  return segments.every((s) => s !== '');
}

function components(value: string): URL | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // `URLComponents(string:)` refuses what is not already a valid URL string (a bare space,
  // for one); the WHATWG parser would percent-encode it instead, so refuse it here first.
  if (/\s/u.test(trimmed)) return null;
  try {
    return new URL(trimmed);
  } catch {
    return null;
  }
}

function sameOrigin(link: URL, server: URL): boolean {
  if (link.protocol.toLowerCase() !== server.protocol.toLowerCase()) return false;
  const serverHost = server.hostname;
  if (serverHost === '') return false;
  if (link.hostname.toLowerCase() !== serverHost.toLowerCase()) return false;
  return effectivePort(link) === effectivePort(server);
}

function effectivePort(url: URL): number {
  if (url.port !== '') return Number(url.port);
  return url.protocol.toLowerCase() === 'https:' ? 443 : 80;
}

function pathSegments(url: URL): string[] {
  return url.pathname
    .split('/')
    .filter((s) => s !== '')
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });
}
